import {
  Button,
  ButtonGroup,
  Fieldset,
  Flex,
  Input,
  Label,
  View,
} from "@aws-amplify/ui-react"
import {
  OrganizationSchemaNameMaxLength,
  OrgSettingsUpdateSchema,
} from "@strtmn/sds/schemas"
import type { OrganizationDto, OrgSettingsUpdateDto } from "@strtmn/sds/types"
import { I18n } from "aws-amplify/utils"
import { useContext, useState } from "react"
import { FormProvider, useForm, type SubmitHandler } from "react-hook-form"
import { FaXmark } from "react-icons/fa6"
import { Link, useNavigate, useParams } from "react-router-dom"
import { updateOrgSettings } from "../../api/OrgsApi"
import {
  CancelButton,
  LoadingErrorState,
  OneTimeSuccessMessage,
} from "../../components"
import config, { isProd } from "../../config"
import { UserDataContext, UserDataContextHelper } from "../../contexts"
import { useIsReadOnly } from "../../hooks"
import { getLinkToOrg } from "../../LinkUtils"
import { imageHash } from "./-components/ListItem"

import "./RecipeManagement.css"

const NUM_ORG_IMAGES = config.images.orgs.numImages
const IMAGE_HASH_SEED = config.images.orgs.imageHashSeed

export default function OrgSettings() {
  const params = useParams()
  const navigate = useNavigate()
  const { userData, reloadUserDataAsync } = useContext(UserDataContext)
  // const rootData = useRouteLoaderData("root") as UserDataDto
  // console.log("🧪 OrgDashboard / rootData:", rootData)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const isReadOnly = useIsReadOnly()
  const [org, setOrg] = useState<OrganizationDto | undefined>(undefined)
  const methods = useForm<OrganizationDto>({
    values: org,
    mode: "onBlur",
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods

  if (!userData) {
    return (
      <LoadingErrorState
        isLoading={true}
        loadingMessage={
          isProd ? I18n.get("Lade Daten ...") : "Loading (userData) ..."
        }
        isError={false}
        errorMessage=""
      />
    )
  }

  let org_: OrganizationDto | undefined = undefined
  if (userData) {
    org_ = UserDataContextHelper.findOrg(params["orgId"]!, userData)
  }
  if (!org) {
    setOrg(org_)
  }
  if (!org_ || !org) {
    return (
      <LoadingErrorState
        isLoading={true}
        loadingMessage={
          isProd ? I18n.get("Lade Daten ...") : "Loading (org) ..."
        }
        isError={false}
        errorMessage=""
      />
    )
  }

  const onSubmit: SubmitHandler<OrganizationDto> = async (data) => {
    console.log("▶️ onSubmit")
    console.log("data === org:", data === org)
    console.log(data)

    if (isReadOnly) {
      console.log("🚧 Read-only mode.")
      return
    }
    setIsFormSubmitting(true)

    const cleaned = cleanData(data)
    // console.debug("< cleanData")
    console.log("cleaned", cleaned)
    console.log("address =", JSON.stringify(cleaned.address))

    let validated: OrgSettingsUpdateDto | undefined = undefined
    if (OrgSettingsUpdateSchema.isValidSync(cleaned)) {
      console.log("✅ Cleaned data is valid")
      validated = cleaned
    } else {
      console.warn("❌ Cleaned data is not valid")
      try {
        validated = await OrgSettingsUpdateSchema.validate(cleaned, {
          abortEarly: false,
        })
      } catch (err) {
        /** @ts-ignore */
        console.log("Validation errors", err.errors)
      }
    }
    if (!validated) {
      setIsFormSubmitting(false)
      return false
    }

    const result = await updateOrgSettings(org.id, cleaned)
    /** @todo Result is meaningless? */
    /** @todo Error handling */

    if (reloadUserDataAsync) {
      !isProd && console.debug("OrgSettings: Reloading data")
      await reloadUserDataAsync()
    } else {
      console.warn(
        "OrgSettings: Not reloading data because callback is not set"
      )
    }

    navigate(getLinkToOrg(org, true), {
      replace: true,
      state: {
        successMessage: I18n.get("Die Einstellungen wurden gespeichert."),
      },
    })
    setIsFormSubmitting(false)
    console.log("OrgSettings: Navigating back")

    return result
  }

  /** Cleans the form data before submission. */
  function cleanData(data: OrgSettingsUpdateDto): OrgSettingsUpdateDto {
    console.debug("> cleanData")
    const cleaned: OrgSettingsUpdateDto = JSON.parse(JSON.stringify(data))
    /** @ts-ignore */
    delete cleaned.updated_at
    /** @ts-ignore */
    delete cleaned.updated_by
    // clean address
    if (cleaned.address && typeof cleaned.address === "object") {
      cleaned.address.street = cleaned.address.street || ""
      cleaned.address.number = cleaned.address.number || ""
      cleaned.address.zip = cleaned.address.zip || ""
      cleaned.address.city = cleaned.address.city || ""
      cleaned.address.country = cleaned.address.country || ""
      cleaned.address.gps = cleaned.address.gps || null
      if (
        cleaned.address.gps &&
        typeof cleaned.address.gps === "object" &&
        !cleaned.address.gps.lat &&
        !cleaned.address.gps.lng
      ) {
        cleaned.address.gps = null
      }
    }
    // console.log("address =", cleaned.address)
    // console.debug("▶️ cleanData > validateSync")
    try {
      return OrgSettingsUpdateSchema.validateSync(cleaned, {
        stripUnknown: true,
      })
    } catch {
      console.warn(
        "cleanData: Validation failed, returning cleaned object, unstripped."
      )
      return cleaned
    }
  }

  !isProd && console.log("🪅🪅🪅 OrgSettings: errors =", errors)
  const imageIdx = imageHash(IMAGE_HASH_SEED, org.id, NUM_ORG_IMAGES)
  const imageSrc =
    org.name === "Andreas" || org.name === "Haggeney"
      ? `/images/orgs/hof-pixabay-1.jpg`
      : org.name === "Heiner" || org.name === "Große Börding"
        ? `/images/orgs/hof-pixabay-2.jpg`
        : `/images/orgs/hof-pixabay-${imageIdx}.jpg`
  return (
    <>
      <div className="rm-view-page">
        <div
          className="hdr-img"
          style={{ backgroundImage: `url('${imageSrc}')` }}
        />
        <Link to="..">
          <Button className="button-close has-icon">
            <span role="img" aria-label="Schließen">
              <FaXmark />
            </span>
          </Button>
        </Link>
        <div className="content-overlay">
          <div className="header">
            <div className="info">
              <h2>{org.name} &mdash; Einstellungen für den Betrieb</h2>
            </div>
          </div>

          <OneTimeSuccessMessage />

          <div className="details">
            {/* pass all methods into the context */}
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/** @todo: {...register("fieldname")} */}
                <Fieldset
                  legend="Name des Betriebs"
                  variation="outlined"
                  size="small"
                >
                  <Input
                    type="text"
                    size="small"
                    placeholder="Name des Betriebs"
                    defaultValue={org.name}
                    {...register("name", {
                      required: "Geben Sie den Namen des Betriebs an.",
                      maxLength: {
                        value: OrganizationSchemaNameMaxLength,
                        message: `Der Name darf max. ${OrganizationSchemaNameMaxLength} Zeichen lang sein.`,
                      },
                    })}
                    className={errors?.name ? "error" : ""}
                  />
                  {errors?.name && (
                    <div className="error">Fehler: {errors.name.message}</div>
                  )}
                </Fieldset>

                <Fieldset
                  legend="Adresse"
                  variation="outlined"
                  size="small"
                  className="!mt-4"
                >
                  <Flex direction="row">
                    <Input
                      type="text"
                      size="small"
                      placeholder="Straße"
                      {...register(
                        "address.street" /*{ required: "Geben Sie die Straße an." }*/
                      )}
                      // className={errors?.address?.street ? "error" : ""}
                    />
                    <Input
                      type="text"
                      size="small"
                      placeholder="Hausnummer"
                      maxLength={6}
                      width="fit-content"
                      {...register(
                        "address.number" /*{ required: "Geben Sie die Hausnummer an." }*/
                      )}
                      // className={errors?.address?.number ? "error" : ""}
                    />
                  </Flex>
                  <Flex direction="row">
                    <Input
                      type="text"
                      size="small"
                      placeholder="PLZ"
                      maxLength={8}
                      width="fit-content"
                      {...register(
                        "address.zip" /*{ required: "Geben Sie die Postleitzahl an." }*/
                      )}
                      // className={errors?.address.zip ? "error" : ""}
                    />
                    <Input
                      type="text"
                      size="small"
                      placeholder="Ort"
                      {...register(
                        "address.city" /*{ required: "Geben Sie den Ort an." }*/
                      )}
                      // className={errors?.address?.city ? "error" : ""}
                    />
                  </Flex>
                  <Input
                    type="text"
                    size="small"
                    placeholder="Land"
                    {...register(
                      "address.country" /*{ required: "Geben Sie das Land an." }*/
                    )}
                    // className={errors?.address.country ? "error" : ""}
                  />

                  <View fontSize="small">
                    Hier kann man eine Adresse in GPS-Koordinaten umrechnen:{" "}
                    <a href="https://www.latlong.net/convert-address-to-lat-long.html">
                      https://www.latlong.net/convert-address-to-lat-long.html
                    </a>
                  </View>
                  <Label htmlFor="address.gps.lat">Latitude</Label>
                  <Input
                    id="address.gps.lat"
                    type="text"
                    size="small"
                    placeholder="Latitude"
                    {...register(
                      "address.gps.lat" /*{ required: "Geben Sie das Land an." }*/
                    )}
                    // className={errors?.address.country ? "error" : ""}
                  />
                  <Label htmlFor="address.gps.lng">Longitude</Label>
                  <Input
                    id="address.gps.lng"
                    type="text"
                    size="small"
                    placeholder="Longitude"
                    {...register(
                      "address.gps.lng" /*{ required: "Geben Sie das Land an." }*/
                    )}
                    // className={errors?.address.country ? "error" : ""}
                  />
                </Fieldset>

                {/** @todo: Add section for drivers */}

                <Fieldset
                  legend="Einstellungen"
                  variation="outlined"
                  size="small"
                  className="!mt-4"
                >
                  <View fontSize="small" fontWeight="bold">
                    Gewichtsangaben in Rezepten nach Frischmasse oder nach
                    TS-Gehalt?
                  </View>
                  <View fontSize="small">
                    Die betriebsweite Einstellung wurde ersetzt.
                  </View>
                  <View fontSize="small">
                    Die Gewichtsangaben in Rezepten werden je Futterkomponente
                    berechnet: Ist bei einer Komponente ein TS-Gehalt größer
                    0&nbsp;% hinterlegt, kann man für diese Komponente
                    Frischmasse und Trockenmasse bearbeiten. Das jeweils andere
                    Gewicht wird automatisch umgerechnet. Ist der TS-Gehalt
                    0&nbsp;%, gilt die Menge direkt als Frischmasse. Den
                    TS-Gehalt legen Sie pro Komponente unter „Komponenten“ fest.
                  </View>

                  {/* <RadioGroupField size="small" isRequired={true} legend="Gewichtseinheiten" name="units">
                    <Radio value="kg">metrisch / kg</Radio>
                    <Radio value="lb">angloamerikanisch / lb</Radio>
                  </RadioGroupField>

                  <SelectField label="Währungszeichen" size="small">
                    <option value="EUR">EUR - € Euro</option>
                    {/ * <option value="CHF">CHF - Schweizer Franken</option >
                <option value="USD">USD - $ Dollar</option>
                <option value="GBP">GBP - £ Pound</option>
                <option value="CNY">CNY - ¥ Yuan Renminbi</option>
                <option value="JPY">JPY - ¥ Yen</option>
                <option value="INR">INR - ₹ Rupee</option> * /}
                  </SelectField> */}
                </Fieldset>

                <ButtonGroup className="buttons !mt-4" size="small">
                  <Button
                    type="submit"
                    variation="primary"
                    isDisabled={isFormSubmitting || isReadOnly}
                    isLoading={isFormSubmitting}
                    loadingText={I18n.get("Wird gespeichert...")}
                  >
                    Speichern
                  </Button>
                  <CancelButton
                    href=".."
                    title="Abbrechen"
                    isDisabled={isFormSubmitting}
                  />
                </ButtonGroup>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>

      {/* <small>* Die Icons "pen-to-square (light)" benötigen FontAwesome Pro, kostet 49,- oder 99,- pro Jahr.</small> */}
    </>
  )
}
